import { keyframes, styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  ReportNotice,
  dismissReportNotice,
  subscribeToReportNotices,
} from "../../Utils/Common/ReportNotifier";

/**
 * Report status cards, stacked under the header in the top right corner.
 *
 * Kept out of react-toastify on purpose: the ToastContainer is centred and
 * shows one toast at a time, while these cards belong to a slower flow that
 * spans minutes and has to be raised from the SignalR service as well.
 */

const PRIMARY = "#69A54B";
const DANGER = "#f1416c";

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(16px); }
  to { opacity: 1; transform: none; }
`;

const Stack = styled("div")({
  position: "fixed",
  top: 16,
  right: 16,
  // Above the app header and the side drawers, below the centred toasts
  zIndex: 1400,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  width: 330,
  maxWidth: "calc(100vw - 36px)",
  pointerEvents: "none",
});

const Card = styled("div")({
  pointerEvents: "auto",
  display: "flex",
  gap: 10,
  padding: "12px 13px",
  borderRadius: 10,
  backgroundColor: "#fff",
  border: "1px solid #eef0f3",
  boxShadow: "0 8px 26px rgba(24, 28, 50, 0.13)",
  animation: `${slideIn} 0.24s ease both`,
});

const Title = styled("div")({
  fontSize: 13,
  fontWeight: 600,
  color: "#181c32",
  lineHeight: 1.3,
});

const Message = styled("div")({
  fontSize: 12,
  color: "#7e8299",
  lineHeight: 1.4,
  marginTop: 2,
  wordBreak: "break-word",
});

const CloseButton = styled("button")({
  border: "none",
  background: "none",
  padding: 0,
  marginLeft: 2,
  fontSize: 15,
  lineHeight: 1,
  color: "#a1a5b7",
  cursor: "pointer",
  alignSelf: "flex-start",
  "&:hover": { color: "#7e8299" },
});

const Spinner = styled("span")({
  width: 15,
  height: 15,
  marginTop: 1,
  flexShrink: 0,
  borderRadius: "50%",
  border: `2px solid ${PRIMARY}33`,
  borderTopColor: PRIMARY,
  animation: "spin 0.7s linear infinite",
  "@keyframes spin": { to: { transform: "rotate(360deg)" } },
});

const StatusIcon = ({ status }: { status: ReportNotice["status"] }) => {
  if (status === "pending") {
    return <Spinner aria-hidden="true" />;
  }
  if (status === "error") {
    return (
      <i
        className="bi bi-exclamation-circle-fill"
        style={{ color: DANGER, fontSize: 15, lineHeight: 1.2 }}
        aria-hidden="true"
      />
    );
  }
  return (
    <i
      className={
        status === "ready"
          ? "bi bi-check-circle-fill"
          : "bi bi-hourglass-split"
      }
      style={{ color: PRIMARY, fontSize: 15, lineHeight: 1.2 }}
      aria-hidden="true"
    />
  );
};

/** One card, owning its own auto dismiss timer */
const NoticeCard = ({ notice }: { notice: ReportNotice }) => {
  const { id, status, title, message, autoCloseMs } = notice;

  useEffect(() => {
    if (!autoCloseMs) {
      return;
    }
    const timer = setTimeout(() => dismissReportNotice(id), autoCloseMs);
    return () => clearTimeout(timer);
    // Restarts the countdown whenever the card is updated in place
  }, [id, status, title, message, autoCloseMs]);

  return (
    <Card role="status" aria-live="polite">
      <StatusIcon status={status} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <Title>{title}</Title>
        {message ? <Message>{message}</Message> : null}
      </div>
      <CloseButton
        type="button"
        aria-label="Close"
        onClick={() => dismissReportNotice(id)}
      >
        <i className="bi bi-x" />
      </CloseButton>
    </Card>
  );
};

const ReportNoticeStack = () => {
  const [notices, setNotices] = useState<ReportNotice[]>([]);

  useEffect(() => subscribeToReportNotices(setNotices), []);

  if (!notices.length) {
    return null;
  }

  // Rendered into the body: a transformed ancestor would otherwise become the
  // containing block and pull the fixed stack away from the viewport corner
  return createPortal(
    <Stack>
      {notices.map((notice) => (
        <NoticeCard key={notice.id} notice={notice} />
      ))}
    </Stack>,
    document.body
  );
};

export default ReportNoticeStack;
